import dayjs from 'dayjs'
import React from 'react'
import { Link } from 'react-router-dom'
import mixpanel from '@disco/api/mixpanel'
import type { CampaignStatus } from '@disco/constants/CampaignTypes'
import {
  STATUS_DRAFT,
  STATUS_SCHEDULED,
  STATUS_SENT,
} from '@disco/constants/CampaignTypes'
import { getCampaignLink } from '@disco/state-management/selectors/campaigns'
import Button from 'components/ui/button'
import type { Column } from 'components/ui/data-table'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Icon, { IconKey } from 'components/ui/icon'
const EN_SPACE = ' '
const getColumn = {
  subject: (status: CampaignStatus, sortable: boolean): Column => {
    let iconType: IconKey = 'email-draft'

    if (status === STATUS_SCHEDULED) {
      iconType = 'email-scheduled'
    }

    if (status === STATUS_SENT) {
      iconType = 'email-sent'
    }

    return {
      name: 'Name',
      width: 'large',
      sortable,
      toSortable: sortable
        ? (prop: Record<string, any>) => prop.name
        : undefined,
      render: (prop: Record<string, any>) => (
        <Link
          className="ContactCell_Link"
          to={getCampaignLink(prop as any)}
          onClick={() => {
            if (status === STATUS_DRAFT) {
              mixpanel.sendPureEvent('Email Creator - Edit draft email')
            }
          }}
        >
          <span>
            <Icon type={iconType} />
          </span>
          <span>
            <span>{EN_SPACE}</span>
            <span>{prop.name || `Untitled ${prop.type}`}</span>
          </span>
        </Link>
      ),
    }
  },
  time: (status: CampaignStatus, sortable: boolean): Column => {
    if (status === STATUS_DRAFT) {
      return {
        name: 'Last edited',
        type: 'date',
        width: 'large',
        sortable,
        toSortable: sortable
          ? (prop: Record<string, any>) => prop.last_modified
          : undefined,
        render: (prop: Record<string, any>) =>
          dayjs(prop.last_modified).format('MMMM D, YYYY'),
      }
    }

    if (status === STATUS_SCHEDULED) {
      return {
        name: 'Scheduled for',
        width: 'large',
        sortable,
        toSortable: sortable
          ? (prop: Record<string, any>) =>
              dayjs.tz(dayjs(prop.scheduled_at), prop.scheduled_at_timezone)
          : undefined,
        render: (prop: Record<string, any>) => {
          const date = dayjs.tz(
            dayjs(prop.scheduled_at),
            prop.scheduled_at_timezone,
          )
          return `${date.format('MMM D, YYYY')} at ${date.format(
            'hh:mm A',
          )} (GMT${date.format('Z')})`
        },
      }
    }

    return {
      name: 'Sent on',
      type: 'date',
      width: 'large',
      sortable,
      toSortable: sortable
        ? (prop: Record<string, any>) => prop.sent_at
        : undefined,
      render: (prop: Record<string, any>) =>
        dayjs(prop.sent_at).format('MMMM D, YYYY'),
    }
  },
  recipients: (sortable: boolean): Column => ({
    name: 'Recipients',
    type: 'numeric',
    width: 'medium',
    sortable,
    toSortable: sortable
      ? (prop: Record<string, any>) => prop.recipients_count
      : undefined,
    render: (prop: Record<string, any>) => prop.recipients_count,
  }),
  stats: (status: CampaignStatus, sortable: boolean): Column => {
    if (status === STATUS_SENT) {
      return {
        contentType: 'button',
        name: 'Stats',
        width: 'content',
        sortable,
        toSortable: sortable
          ? (prop: Record<string, any>) => prop.status
          : undefined,
        render: (prop: Record<string, any>) => (
          <Button
            appearance="icon"
            icon="stats-lite"
            tooltip="View stats"
            disabled={prop.status !== 'sent'}
            link={`/campaigns/${prop.id}/stats`}
            onClick={() => {
              mixpanel.sendPureEvent('Email Creator - View email stats')
            }}
          />
        ),
      }
    } else {
      return {
        width: 'content',
      }
    }
  },
  actions: (
    status: CampaignStatus,
    sortable: boolean,
    isFeatureEnabled: boolean,
    onActionClick: (...args: Array<any>) => any,
  ): Column => {
    return {
      contentType: 'button',
      name: 'Actions',
      width: 'content',
      render: (prop: Record<string, any>) => {
        const options: {
          label: string
          value: string
          destructive?: boolean
        }[] = [
          {
            label: 'Duplicate',
            value: 'Duplicate',
          },
        ]

        if (prop.status === 'scheduled') {
          options.push(
            {
              label: 'Reschedule',
              value: 'Reschedule',
            },
            {
              label: 'Cancel Schedule',
              value: 'Cancel Schedule',
            },
          )
        }

        options.push({
          label: 'Delete',
          value: 'Delete',
          destructive: true,
        })
        return (
          <EllipsisMenu
            compact
            disabled={!isFeatureEnabled}
            options={options}
            icon="ellipsis"
            hasArrow={false}
            onOptionClick={value => onActionClick(prop, value)}
          />
        )
      },
    }
  },
}
export const getCampaignColumns = ({
  status,
  isFeatureEnabled,
  onActionClick,
  sortable,
}: {
  status: CampaignStatus
  isFeatureEnabled: boolean
  onActionClick: (...args: Array<any>) => any
  sortable: boolean
}): Column[] => {
  return [
    getColumn.subject(status, sortable),
    getColumn.time(status, sortable),
    getColumn.recipients(sortable),
    getColumn.stats(status, sortable),
    getColumn.actions(status, sortable, isFeatureEnabled, onActionClick),
  ]
}
